import React from "react";
import { Box, useMediaQuery, useTheme, Typography, Chip } from "@mui/material";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import {
  CustomSectionTitle,
  CustomTable,
  MobileTable,
  type CustomColumnDef,
  type CustomCellParams,
  CustomSingleSelect,
  CustomTruncateText,
  CustomChip,
  CustomLoading,
  colors,
} from "@ippon/shared-components";
import { getAiAssessmentColors } from "../../../theme/aiAssessment";
import { CustomListToolbar } from "../../ListToolbar/CustomListToolbar";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { AppDispatch, RootState } from "../../../store";
import {
  fetchEstimatesByClient,
  updateEstimateInList,
} from "../../../store/repairSlice";
import ipponApi from "../../../config/axios";
import { getClientID } from "../../../utils/authUtils";
import { FILTER_FETCH_LIMIT } from "../../../constants/listFilter";
import { useFilteredPagination } from "../../../hooks/useFilteredPagination";
import { Pagination } from "../../PropertyList/Pagination";
import {
  AREA_TYPE_LABELS,
  AREA_TYPE_OPTIONS,
  type AreaType,
} from "@ippon/shared-types";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import {
  ESTIMATE_LIST_LOADING_MIN_HEIGHT,
  resolveEstimateListRows,
} from "./estimateListLoading";

// Date formatting utility
const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const formatDateTime = (input: string | Date): string => {
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${y}/${m}/${day} ${hh}:${mm}`;
};
const FILTER_INPUT_WIDTH = "124px";
const FILTER_INPUT_HEIGHT = "34px";

const LABEL_TO_AREA_TYPE: Record<string, AreaType> = Object.fromEntries(
  (Object.entries(AREA_TYPE_LABELS) as [AreaType, string][]).map(
    ([code, label]) => [label, code],
  ),
);

const normalizeAreaType = (raw: unknown): AreaType | "" => {
  if (typeof raw !== "string" || raw === "") return "";
  if (raw in AREA_TYPE_LABELS) return raw as AreaType;
  if (raw in LABEL_TO_AREA_TYPE) return LABEL_TO_AREA_TYPE[raw];
  return "";
};

const getPropertyCategoryLabel = (value: string | undefined): string => {
  if (value === "residence") return "レジデンス";
  if (value === "building") return "ビルディング";
  return "-";
};

/** Chip の border(左右) + label の padding(左右) の合計 */
const CHIP_BORDER_PX = 2;
const CHIP_LABEL_PADDING_PX = 11;
const CHIP_INNER_OFFSET = (CHIP_BORDER_PX + CHIP_LABEL_PADDING_PX) * 2;

const AiResultChip: React.FC<{ value: string }> = ({ value }) => {
  const boxRef = React.useRef<HTMLDivElement>(null);
  if (!value || value === "-") return <span>-</span>;
  const scheme = getAiAssessmentColors(value);
  return (
    <Box
      ref={boxRef}
      display="flex"
      justifyContent="flex-start"
      width="100%"
      height="100%"
      alignItems="center"
    >
      <Chip
        label={
          <CustomTruncateText
            text={value}
            ellipsis="..."
            containerRef={boxRef}
            containerOffset={CHIP_INNER_OFFSET}
          />
        }
        variant="outlined"
        sx={{
          borderRadius: "8px",
          fontSize: "12px",
          minWidth: { xs: "60px", sm: "80px" },
          maxWidth: "100%",
          height: { xs: "23px", sm: "32px" },
          justifyContent: "center",
          color: scheme.color,
          backgroundColor: scheme.backgroundColor,
          borderColor: scheme.borderColor,
          fontWeight: "bold",
          cursor: "default",
          "&:hover": {
            backgroundColor: scheme.backgroundColor,
            borderColor: scheme.borderColor,
          },
          "&:focus": {
            backgroundColor: scheme.backgroundColor,
            borderColor: scheme.borderColor,
          },
          "& .MuiChip-label": {
            padding: { xs: "0 8px", sm: "0 11px" },
            overflow: "hidden",
          },
        }}
      />
    </Box>
  );
};

type FormValues = {
  keyword: string;
  category?: string;
  areaType?: AreaType | "";
  name?: string;
  aiResult?: string;
};

type PerPageForm = { perPage: number };

type Row = {
  id: string | number;
  title: string;
  propertyName: string;
  propertyCategory: string; // "residence" | "building" | ""
  category: string;
  areaType: AreaType | "";
  detail: string;
  registeredAt: string;
  aiResult: string;
  repairId?: string;
  estimateData?: any;
};

const isEstimateListRowDimmed = (row: Row) =>
  row.aiResult === "査定失敗" || row.aiResult === "査定中";

const renderEstimateListTextCellWithDash = (
  params: CustomCellParams,
): React.ReactNode => {
  const { value } = params;
  const row = params.row as Row;
  const dimmed = isEstimateListRowDimmed(row);
  const text = value !== null && value !== undefined ? String(value) : "";
  const trimmed = text.trim();
  return (
    <Box
      sx={{
        width: "100%",
        fontSize: 14,
        fontWeight: 400,
        color: dimmed ? "#989898" : "#3e3e3e",
      }}
    >
      <CustomTruncateText text={trimmed || "-"} />
    </Box>
  );
};

// Figma準拠のフィルターアイテムコンポーネント
const FilterItem: React.FC<{
  label: string;
  labelWidth: string;
  children: React.ReactNode;
}> = ({ label, labelWidth, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <Typography
      sx={{
        width: labelWidth,
        fontSize: "12px",
        fontWeight: 700,
        color: "#3e3e3e",
        textAlign: "left",
        whiteSpace: "nowrap",
        flexShrink: 0,
        height: { sm: "34px", xs: "32px" },
        display: "flex",
        alignItems: "center",
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

export const EstimateList: React.FC = () => {
  const { register, watch, formState, reset, control, setValue } =
    useForm<FormValues>({
      defaultValues: {
        keyword: "",
        category: "",
        areaType: "",
        name: "",
        aiResult: "",
      },
    });
  const { control: perPageControl } = useForm<PerPageForm>({
    defaultValues: { perPage: 50 },
  });
  // 検索条件・perPage は離脱確認対象外のため CustomFormNavigationGuard は使用していない
  const dispatch = useDispatch<AppDispatch>();
  const estimateList = useSelector((s: RootState) => s.repairs.estimateList);
  const repairs = estimateList.data;
  const total = estimateList.total;
  const loading = estimateList.loading;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  // セレクト幅はブレークポイントで変えず常に固定 (1200 で縮めない)。
  const filterInputWidth = FILTER_INPUT_WIDTH;

  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState<string>("");
  const [selectedAreaType, setSelectedAreaType] = React.useState<AreaType | "">(
    "",
  );
  const [nameFilter, setNameFilter] = React.useState<string>("");
  const [selectedAiResult, setSelectedAiResult] = React.useState<string>("");
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = React.useState<number>(50);
  const [initialLoadError, setInitialLoadError] =
    React.useState<ApiErrorType | null>(null);

  const hasActiveFilter = !!(
    searchTerm ||
    selectedCategory ||
    selectedAreaType ||
    nameFilter ||
    selectedAiResult
  );

  const perPageOptions = React.useMemo(
    () => [
      { value: 10, label: "10件" },
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" },
    ],
    [],
  );

  const fetchDetail = React.useCallback(async () => {
    setInitialLoadError(null);
    const cid = getClientID();
    if (!cid) {
      setInitialLoadError("unknown");
      return;
    }
    const result = hasActiveFilter
      ? await dispatch(
          fetchEstimatesByClient({
            client_id: cid,
            page: 1,
            limit: FILTER_FETCH_LIMIT,
          }) as any,
        )
      : await dispatch(
          fetchEstimatesByClient({
            client_id: cid,
            page: currentPage,
            limit: itemsPerPage,
          }) as any,
        );

    if (fetchEstimatesByClient.rejected.match(result)) {
      const payload = result.payload as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
    }
  }, [dispatch, currentPage, itemsPerPage, hasActiveFilter]);

  // フィルター未使用時: サーバーサイドページネーション
  // フィルター使用時: 全件取得してクライアントサイドでフィルタ＋ページネーション
  React.useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const keyword = watch("keyword");
  const wCategory = watch("category");
  const wAreaType = watch("areaType");
  const wName = watch("name");
  const wAiResult = watch("aiResult");

  const handleSearch = () => {
    setSearchTerm(keyword || "");
    setSelectedCategory(wCategory || "");
    setSelectedAreaType((wAreaType as any) || "");
    setNameFilter(wName || "");
    setSelectedAiResult(wAiResult || "");
    setCurrentPage(1);
  };

  const handleClear = () => {
    reset({
      keyword: "",
      category: "",
      areaType: "",
      name: "",
      aiResult: "",
    });
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedAreaType("");
    setNameFilter("");
    setSelectedAiResult("");
    setCurrentPage(1);
  };

  const flatRows: Row[] = React.useMemo(() => {
    const list = Array.isArray(repairs) ? repairs : [];
    const out: Row[] = [];
    list.forEach((repair: any) => {
      const areaType = normalizeAreaType(repair.area_type);
      const detail =
        areaType === "private" && repair.room_number
          ? String(repair.room_number)
          : repair.area_name;
      const category = Array.isArray(repair.category)
        ? repair.category.join(", ")
        : repair.category || "";
      const registeredAt = repair.created_at;
      const estimates = Array.isArray(repair.repair_estimates)
        ? repair.repair_estimates
        : [];
      if (estimates.length === 0) {
        return;
      }
      estimates.forEach((e: any) => {
        const getCleanTitle = (fileName: string): string => {
          if (!fileName) return "";
          return fileName
            .replace(/^[a-f0-9-]+_/, "")
            .replace(/^【御見積】/, "")
            .replace(/\.pdf$/, "")
            .replace(/ \(\d+\)$/, "");
        };

        out.push({
          id: e.repair_estimate_id || `${repair.repair_id}-${out.length}`,
          title: getCleanTitle(e.file_name) || repair.repair_title || "",
          propertyName: repair.property_name || "",
          propertyCategory: repair.property_category || "",
          category,
          areaType,
          detail,
          registeredAt,
          aiResult: e.ai_error
            ? "査定失敗"
            : e.ai_overall_decision || (e.ai_assessed ? "査定済" : "査定中"),
          repairId: repair.repair_id,
          estimateData: e,
        });
      });
    });
    return out;
  }, [repairs]);

  // 「査定中」の行だけ個別にポーリングして差分更新
  const assessingRows = React.useMemo(
    () => flatRows.filter((r) => r.aiResult === "査定中"),
    [flatRows],
  );
  const assessingRowsRef = React.useRef(assessingRows);
  assessingRowsRef.current = assessingRows;

  React.useEffect(() => {
    if (assessingRows.length === 0) return;

    const BaseURL = import.meta.env.VITE_API_URL;
    let cancelled = false;
    let timerId: ReturnType<typeof setInterval> | null = null;

    const pollEstimates = async () => {
      if (cancelled) return;
      const rows = assessingRowsRef.current;
      if (rows.length === 0) {
        if (timerId != null) {
          clearInterval(timerId);
          timerId = null;
        }
        return;
      }
      for (const row of rows) {
        if (cancelled) break;
        try {
          const res = await ipponApi.get(
            `${BaseURL}/api/v1/repairs/${row.repairId}/estimates/${row.id}`,
          );
          const est = res.data?.data;
          if (est && (est.ai_assessed || est.ai_error)) {
            dispatch(
              updateEstimateInList({
                repair_id: row.repairId!,
                repair_estimate_id: String(row.id),
                estimate: est,
              }),
            );
          }
        } catch (error) {
          console.error(
            `Error during estimate polling (repairId=${row.repairId}, estimateId=${row.id}):`,
            error,
          );
        }
      }
    };

    timerId = setInterval(pollEstimates, 5000);

    return () => {
      cancelled = true;
      if (timerId != null) {
        clearInterval(timerId);
      }
    };
  }, [assessingRows, dispatch]);

  const categoryOptions = React.useMemo(() => {
    const set = new Set<string>();
    (Array.isArray(repairs) ? repairs : []).forEach((r: any) => {
      const cats = Array.isArray(r.category)
        ? r.category
        : r.category
          ? [r.category]
          : [];
      cats.forEach((c: string) => set.add(String(c)));
    });
    return Array.from(set).map((c) => ({ value: c, label: c }));
  }, [repairs]);

  const nameOptions = React.useMemo(() => {
    const set = new Set<string>();
    flatRows.forEach((r) => {
      if (r.detail) set.add(String(r.detail));
    });
    return Array.from(set).map((n) => ({ value: n, label: n }));
  }, [flatRows]);

  const aiResultOptions = React.useMemo(() => {
    const set = new Set<string>();
    flatRows.forEach((r) => {
      if (r.aiResult) set.add(r.aiResult);
    });
    ["査定済", "査定中"].forEach((s) => set.add(s));
    return Array.from(set).map((s) => ({ value: s, label: s }));
  }, [flatRows]);

  const rows = React.useMemo(() => {
    const k = (searchTerm || "").toLowerCase();
    return flatRows
      .filter((r) => {
        if (!k) return true;
        const areaLabel = r.areaType
          ? AREA_TYPE_LABELS[r.areaType] || r.areaType
          : "";
        return [r.title, r.category, areaLabel, r.detail, r.aiResult]
          .join(" ")
          .toLowerCase()
          .includes(k);
      })
      .filter((r) =>
        selectedCategory
          ? r.category.split(", ").includes(selectedCategory)
          : true,
      )
      .filter((r) =>
        selectedAreaType ? r.areaType === selectedAreaType : true,
      )
      .filter((r) =>
        nameFilter
          ? String(r.detail || "")
              .toLowerCase()
              .includes(nameFilter.toLowerCase())
          : true,
      )
      .filter((r) =>
        selectedAiResult ? r.aiResult === selectedAiResult : true,
      );
  }, [
    flatRows,
    searchTerm,
    selectedCategory,
    selectedAreaType,
    nameFilter,
    selectedAiResult,
  ]);

  const { paginatedRows, totalPages } = useFilteredPagination({
    hasActiveFilter,
    rows,
    serverTotal: total || 0,
    currentPage,
    perPage: itemsPerPage,
  });
  const hasEstimateListCache = estimateList.loaded || flatRows.length > 0;
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasEstimateListCache,
    dedupeKey: `estimates:${currentPage}:${itemsPerPage}:${hasActiveFilter}`,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // 1ページ目に戻す
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    selectedCategory,
    selectedAreaType,
    nameFilter,
    selectedAiResult,
  ]);

  const handleRowClick = (row: any) => {
    navigate(`/estimates/${row.id}`, {
      state: {
        companyName: row.estimateData?.estimate_company_name || "会社名不明",
        title: row.title,
        amount: row.estimateData?.estimate_amount || 0,
        status: row.estimateData?.status || "待機中",
        repair_image: row.estimateData?.repair_image,
        detailed_evaluation: row.estimateData?.detailed_evaluation,
        anomaly_checks: row.estimateData?.anomaly_checks,
        ai_assessed: row.estimateData?.ai_assessed,
        repairId: row.repairId,
        fromEstimateList: true,
      },
    });
  };

  const columns: CustomColumnDef[] = [
    {
      field: "title",
      headerName: "見積書名",
      width: 260,
      renderCell: (params: CustomCellParams) => {
        const row = params.row as Row;
        const dimmed = isEstimateListRowDimmed(row);
        const isFailed = row.aiResult === "査定失敗";
        const isAssessing = row.aiResult === "査定中";
        const isDisabled = isFailed || isAssessing;
        return (
          <Box
            sx={{
              width: "100%",
              cursor: isDisabled ? "default" : "pointer",
              textDecoration: isDisabled ? "none" : "underline",
              fontSize: 14,
              fontWeight: 400,
              color: dimmed ? "#989898" : "#3e3e3e",
              "&:hover": { color: dimmed ? "#989898" : "#0b9dbd" },
            }}
            onClick={isDisabled ? undefined : () => handleRowClick(params.row)}
          >
            <CustomTruncateText text={params.value} />
          </Box>
        );
      },
    },
    {
      field: "propertyName",
      headerName: "物件名",
      flex: 1,
      tooltipValueGetter: ({ value }) => String(value || ""),
      renderCell: renderEstimateListTextCellWithDash,
    },
    {
      field: "propertyCategory",
      headerName: "種別",
      width: 120,
      tooltipValueGetter: ({ value }) =>
        getPropertyCategoryLabel(value as string | undefined),
      renderCell: (params: CustomCellParams) => {
        const v = params.value as string | undefined;
        const label = getPropertyCategoryLabel(v);
        const dimmed = isEstimateListRowDimmed(params.row as Row);
        return (
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              fontSize: 14,
              fontWeight: 400,
              color: dimmed ? "#989898" : "#3e3e3e",
            }}
          >
            <CustomTruncateText text={label} />
          </Box>
        );
      },
    },
    {
      field: "category",
      headerName: "カテゴリ",
      flex: 1,
      renderCell: renderEstimateListTextCellWithDash,
    },
    {
      field: "areaType",
      headerName: "場所区分",
      flex: 1,
      tooltipValueGetter: ({ value }) => {
        const areaType = normalizeAreaType(value);
        return areaType ? AREA_TYPE_LABELS[areaType] : "-";
      },
      renderCell: (params: CustomCellParams) => {
        const v = params.value as AreaType | "" | undefined;
        if (!v) return "-";
        return (
          <CustomChip
            label={AREA_TYPE_LABELS[v] || v}
            backgroundColor="#F5F5F5"
            color="#666"
            fontSize="12px"
            fontWeight={500}
            height={22}
          />
        );
      },
    },
    {
      field: "detail",
      headerName: "名称",
      flex: 1,
      renderCell: renderEstimateListTextCellWithDash,
    },
    {
      field: "aiResult",
      headerName: "AI査定結果",
      width: 125,
      tooltipValueGetter: ({ value }) => String(value || ""),
      renderCell: (params: CustomCellParams) => (
        <AiResultChip value={params.value} />
      ),
    },
    {
      field: "registeredAt",
      headerName: "登録日時",
      flex: 1,
      renderCell: (params: CustomCellParams) => {
        const dimmed = isEstimateListRowDimmed(params.row as Row);
        return (
          <Box
            sx={{
              width: "100%",
              fontSize: 14,
              fontWeight: 400,
              color: dimmed ? "#989898" : "#3e3e3e",
              textAlign: "left",
            }}
          >
            <CustomTruncateText text={formatDateTime(params.value)} />
          </Box>
        );
      },
    },
  ];

  if (shouldShowInitialLoadError(initialLoadError, hasEstimateListCache)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  return (
    <Box pt={{ xs: "10px", sm: "24px" }}>
      <CustomSectionTitle title="見積書一覧" size="large" />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, auto)",
              md: "repeat(4, auto)",
            },
            columnGap: "8px",
            rowGap: "8px",
            justifyContent: "start",
            alignItems: "start",
            mt: 1,
          }}
        >
          <FilterItem label="カテゴリ" labelWidth={isMdUp ? "auto" : "76px"}>
            <Box sx={{ width: filterInputWidth, height: FILTER_INPUT_HEIGHT }}>
              <CustomSingleSelect
                label=""
                name="category"
                control={control}
                errors={formState.errors}
                options={categoryOptions}
                placeholder="指定なし"
                labelWidth="0px"
                inputWidth={filterInputWidth}
                onChangeValue={(v) => setValue("category", v as any)}
                searchable
                searchPlaceholder="カテゴリ検索"
                hasSpacing={false}
              />
            </Box>
          </FilterItem>

          <FilterItem label="名称" labelWidth={isMdUp ? "auto" : "76px"}>
            <Box sx={{ width: filterInputWidth, height: FILTER_INPUT_HEIGHT }}>
              <CustomSingleSelect
                label=""
                name="name"
                control={control}
                errors={formState.errors}
                options={nameOptions}
                placeholder="指定なし"
                labelWidth="0px"
                inputWidth={filterInputWidth}
                onChangeValue={(v) => setValue("name", v as any)}
                searchable
                searchPlaceholder="名称を検索"
                hasSpacing={false}
              />
            </Box>
          </FilterItem>
          <FilterItem label="AI査定結果" labelWidth={isMdUp ? "auto" : "76px"}>
            <Box sx={{ width: filterInputWidth, height: FILTER_INPUT_HEIGHT }}>
              <CustomSingleSelect
                label=""
                name="aiResult"
                control={control}
                errors={formState.errors}
                options={aiResultOptions}
                placeholder="指定なし"
                labelWidth="0px"
                inputWidth={filterInputWidth}
                onChangeValue={(v) => setValue("aiResult", v as any)}
                hasSpacing={false}
              />
            </Box>
          </FilterItem>
          <FilterItem label="場所区分" labelWidth={isMdUp ? "auto" : "76px"}>
            <Box sx={{ width: filterInputWidth, height: FILTER_INPUT_HEIGHT }}>
              <CustomSingleSelect
                label=""
                name="areaType"
                control={control}
                errors={formState.errors}
                options={AREA_TYPE_OPTIONS as any}
                placeholder="指定なし"
                labelWidth="0px"
                inputWidth={filterInputWidth}
                hasSpacing={false}
                onChangeValue={(v) => setValue("areaType", v as any)}
              />
            </Box>
          </FilterItem>
        </Box>

        <CustomListToolbar
          clearIconHideBetween600And900={true}
          showSearch={true}
          searchPlaceholder="キーワードで絞り込む"
          searchRegister={register}
          searchErrors={formState.errors}
          searchName="keyword"
          onSearch={handleSearch}
          onClear={handleClear}
          perPageOptions={perPageOptions}
          perPageControl={perPageControl}
          perPageName="perPage"
          onPerPageChange={handlePerPageChange}
          showPrimaryButton={false}
          showDelete={false}
        />
      </Box>

      <Box mt={1}>
        {(() => {
          // ヘッダーは常時表示し、ローディングはヘッダー下のみで行う
          // (物件構成一覧と同じ挙動。CustomTable の isLoading が body を覆うオーバーレイを出す)
          return (
            <>
              {isMobile ? (
                loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: ESTIMATE_LIST_LOADING_MIN_HEIGHT,
                      width: "100%",
                    }}
                  >
                    <CustomLoading
                      loading={true}
                      fullScreen={false}
                      size="medium"
                    />
                  </Box>
                ) : (
                  <MobileTable
                    rows={paginatedRows}
                    columns={columns}
                    handlePropertyClick={handleRowClick}
                    disableNameClick={false}
                    checkBoxOpen={false}
                    hidePagination={true}
                    noDataMessage="見積書が登録されていません"
                    noDataIcon={
                      <RequestQuoteOutlinedIcon
                        sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
                      />
                    }
                    getRowSx={(row) =>
                      row.aiResult === "査定失敗" || row.aiResult === "査定中"
                        ? {
                            backgroundColor: "#F5F5F5",
                            // 査定失敗/査定中はグレーアウトのまま (ホバーで白に戻さない=ホバー効果なし)
                            "&:hover": { backgroundColor: "#F5F5F5" },
                          }
                        : undefined
                    }
                    renderTitle={(row) => {
                      const isFailed = row.aiResult === "査定失敗";
                      const isAssessing = row.aiResult === "査定中";
                      const isDisabled = isFailed || isAssessing;
                      return (
                        <Typography
                          variant="body1"
                          onClick={
                            isFailed
                              ? undefined
                              : (e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  handleRowClick(row);
                                }
                          }
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            textDecoration: isFailed ? "none" : "underline",
                            cursor: isFailed ? "default" : "pointer",
                            color: isDisabled ? "#989898" : "#3e3e3e",
                            display: "block",
                            width: "100%",
                          }}
                        >
                          <CustomTruncateText text={row.title || ""} />
                        </Typography>
                      );
                    }}
                  />
                )
              ) : (
                <CustomTable
                  rows={resolveEstimateListRows(paginatedRows, loading)}
                  columns={columns}
                  rowHeight={38}
                  pagination={undefined}
                  hidePagination
                  initialSort={{ field: "registeredAt", direction: "asc" }}
                  minHeight={loading ? ESTIMATE_LIST_LOADING_MIN_HEIGHT : "0px"}
                  isLoading={loading}
                  noDataMessage="見積書が登録されていません"
                  noDataIcon={
                    <RequestQuoteOutlinedIcon
                      sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
                    />
                  }
                  getRowSx={(row) =>
                    row.aiResult === "査定失敗" || row.aiResult === "査定中"
                      ? {
                          backgroundColor: "#F5F5F5",
                          // 査定失敗/査定中はグレーアウトのまま (ホバーで白に戻さない=ホバー効果なし)
                          "&:hover": { backgroundColor: "#F5F5F5" },
                        }
                      : undefined
                  }
                />
              )}

              {!loading && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          );
        })()}
      </Box>
    </Box>
  );
};
